import { Shape } from '@/services/shape';
import { ImageArrayAttribute } from '@/services/shape/attributes/image-array';
import {
  addResourceIfNotExists,
  generateLayoutAst,
  getImageArrayWidgetMaxSize,
  insertImages
} from '../../utils/resource.utils';
import { DataSourceFormat } from '@/resources/enum';
import { generateResourceName, getRefName } from '../../utils/name.utils';

/**
 * 当使用时间等需要拼接的数据源时，用widget包含多个数据源元素，拼接生成ast
 ```
// 支持以下数据源，不涉及多单位
 'weatherCurrentSunRise',
 'weatherCurrentSunSet',
 'appAlarm',
 'time'
 ```
 * @param resourceAst
 * @param shape
 * @returns
 */
export const getImageArrayWidgetTime = (
  resourceAst: Ast,
  shape: Shape,
  hourKey: string,
  minuteKey: string,
  parent: StoreLayerItem | undefined,
  secondKey?: string,
  /** 全局缩放×元素缩放，用于子元素 _scale 与 Widget 尺寸，不传则用 shape.attrs.scale */ effectiveScale?: number
) => {
  const {
    x = 0,
    y = 0,
    resources,
    sourceFormat,
    space = 0,
    recolorEnable = false,
    widgetSpace = 0,
    scale = 1,
    alpha = 1
  } = shape.attrs as ImageArrayAttribute;
  const scaleForChildren = effectiveScale ?? scale;
  // 添加图片
  const imageMap = insertImages(resourceAst, resources, {
    recolorEnable,
    scale: scaleForChildren,
    alpha
  });
  const _dataItemImageNumber = (options: {
    leadingZero: boolean;
    source: string;
  }) => {
    const { source, leadingZero } = options;
    const resourceItem: Ast = {
      tag: 'DataItemImageNumber',
      attrsMap: {
        source: source,
        ref: getRefName(imageMap['images']),
        rotation: '0',
        leadingZero: leadingZero ? 'true' : 'false',
        decimalOffsetX: '0',
        trailingZero: 'false',
        totalDigits: '2',
        decimalDigits: '0',
        supportRecolor: String(recolorEnable),
        parameter: '1000',
        renderRule: 'alwaysShow',
        align: 'left',
        space: String(space)
      },
      children: []
    };

    const ast = addResourceIfNotExists(resourceAst, resourceItem);

    // 生成 layout ast
    const layoutAst: Ast = generateLayoutAst(
      {
        ref: '@' + ast.attrsMap.name,
        x,
        y
      },
      'Item'
    );
    return layoutAst;
  };

  const _imageUnitIcon = () => {
    const layoutAst: Ast = generateLayoutAst(
      {
        ref: getRefName(imageMap['unitIcon']),
        x,
        y
      },
      'Item'
    );
    return layoutAst;
  };

  const child1 = _dataItemImageNumber({
    leadingZero:
      sourceFormat == DataSourceFormat['Format_08:00'] ||
      sourceFormat == DataSourceFormat['Format_08:00:00'],
    source: hourKey
  });
  const child2 = _imageUnitIcon();
  const child3 = _dataItemImageNumber({
    leadingZero: true,
    source: minuteKey
  });

  const hasSeconds =
    sourceFormat == DataSourceFormat['Format_8:00:00'] ||
    sourceFormat == DataSourceFormat['Format_08:00:00'];

  const children = [child1, child2, child3];
  if (hasSeconds && secondKey) {
    const child4 = _imageUnitIcon();
    const child5 = _dataItemImageNumber({
      leadingZero: true,
      source: secondKey
    });
    children.push(child4, child5);
  }

  children.forEach((item, index) => {
    item.attrsMap._id = shape.id + '_' + index;
    // 添加缩放值/透明度（使用 effectiveScale 使图组时间在全局缩放下生效）
    item.attrsMap._scale = String(scaleForChildren);
    item.attrsMap._alpha = String(alpha);
  });
  const { width, height } = getImageArrayWidgetMaxSize({
    resources,
    format: hasSeconds ? '08:00:00' : '08:00',
    space,
    gap: widgetSpace
  });
  const widgetAst: Ast = {
    tag: 'Widget',
    attrsMap: {
      name: generateResourceName(resourceAst, 'Widget'),
      flex_direction: 'row',
      justify_content: 'flex-start',
      align_items: 'center',
      gap: String(widgetSpace),
      w: String(width * scaleForChildren),
      h: String(height * scaleForChildren),
      _asWidget: 'image-array-time'
    },
    children
  };
  if (parent?.attrs.minClickZone) widgetAst.attrsMap.minClickZone = 'true';
  const _ast = addResourceIfNotExists(resourceAst, widgetAst);
  const layoutAst: Ast = generateLayoutAst({
    ref: '@' + _ast.attrsMap.name,
    x,
    y
  });

  return layoutAst;
};
